import React, { useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { scaleQuantize, scaleSqrt } from "d3-scale";

// Mock utils (replace with your actual imports)
const currency = (val: number) => `$${val.toLocaleString()}`;
const US_STATES_GEOJSON =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

export interface StateDataItem {
  state: string;
  value: number;
}

export interface CityDataItem {
  name: string;
  coordinates: [number, number]; // [lon, lat]
  value: number;
  isState?: boolean; // Flag to identify if this is state-level data
}

interface WorldUSSalesMapProps {
  stateData: StateDataItem[];
  cityData?: CityDataItem[];
  width?: number;
  height?: number;
  colorScheme?: string[];
  showLegend?: boolean;
}

const DEFAULT_COLORS = [
  "#e8eef8",
  "#cddcf3",
  "#b1c9ee",
  "#95b6e8",
  "#79a3e3",
  "#5d90de",
  "#417dd8",
  "#2a67c7",
  "#1e4fa0",
  "#153a79",
];

/* ===================== Legend ===================== */
const Legend: React.FC<{
  title?: string;
  subtitle?: string;
}> = ({ title, subtitle }) => {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          width: "min(980px, 100%)",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "20px 24px",
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: "var(--text-primary)",
            marginBottom: 16,
            letterSpacing: "-0.02em",
          }}
        >
          {title ?? "US Sales Map"}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 32,
            flexWrap: "wrap",
          }}
        >
          {/* State Fill Legend */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                display: "flex",
                gap: 2,
                border: "1px solid var(--border-default)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <div style={{ width: 20, height: 20, background: "#e8eef8" }} />
              <div style={{ width: 20, height: 20, background: "#95b6e8" }} />
              <div style={{ width: 20, height: 20, background: "#417dd8" }} />
              <div style={{ width: 20, height: 20, background: "#153a79" }} />
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 500 }}>
              State Sales (low to high)
            </div>
          </div>

          <div
            style={{ width: 1, height: 24, background: "var(--border-default)" }}
          />

          {/* City Marker Legend */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "var(--blue)",
                  border: "1px solid var(--bg-surface)",
                }}
              />
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "var(--blue)",
                  border: "1px solid var(--bg-surface)",
                }}
              />
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "var(--blue)",
                  border: "1px solid var(--bg-surface)",
                }}
              />
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 500 }}>
              City Sales (size represents value)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
interface HoverData extends StateDataItem {
  x: number;
  y: number;
  type: "state" | "city";
  name?: string;
}

/* ===================== Map Component ===================== */
const WorldUSSalesMap: React.FC<WorldUSSalesMapProps> = ({
  stateData,
  cityData = [],
  width = 980,
  height = 600,
  colorScheme = DEFAULT_COLORS,
  showLegend = true,
}) => {
  const [hovered, setHovered] = useState<HoverData | null>(null);

  const onlyStateData = useMemo(
    () => cityData.filter((d) => d.isState),
    [cityData]
  );
  const handleHover = (
    e: React.MouseEvent<SVGElement, MouseEvent>,
    item: StateDataItem,
    type: "state" | "city"
  ) => {
    setHovered({
      ...item,
      type,
      x: e.clientX,
      y: e.clientY,
    });
  };

  // Calculate min/max for color scale
  const [min, max] = useMemo(() => {
    if (!onlyStateData.length) return [0, 0];
    let mn = Number.POSITIVE_INFINITY;
    let mx = Number.NEGATIVE_INFINITY;
    for (const d of onlyStateData) {
      if (typeof d.value === "number") {
        if (d.value < mn) mn = d.value;
        if (d.value > mx) mx = d.value;
      }
    }
    if (!isFinite(mn) || !isFinite(mx)) return [0, 0];
    if (mn === mx) {
      return [0, mx || 1];
    }
    return [mn, mx];
  }, [onlyStateData]);

  // Color scale for states
  const fillScale = useMemo(
    () => scaleQuantize<string>().domain([min, max]).range(colorScheme),
    [min, max, colorScheme]
  );

  // Filter out state-level entries from cityData, only keep actual cities
  const onlyCityData = useMemo(
    () => cityData.filter((d) => !d.isState),
    [cityData]
  );

  // Bubble scale for city markers
  const bubbleScale = useMemo(() => {
    const values = onlyCityData
      .map((c) => c.value)
      .filter((v) => typeof v === "number");
    const cmax = values.length ? Math.max(...values) : 0;
    const hi = cmax || 1;
    return scaleSqrt().domain([0, hi]).range([3, 18]);
  }, [onlyCityData]);
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}
    >
      {showLegend && (
        <Legend
          title="US Sales Map"
          subtitle="Filled color = State value • Circles = City value (size ∝ value)"
        />
      )}

      <ComposableMap
        projection="geoAlbersUsa"
        width={width}
        height={height}
        style={{
          width: "100%",
          height: "auto",
        }}
      >
        <g>
          {/* Render States */}
          <Geographies geography={US_STATES_GEOJSON}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const stateName: string = geo.properties.name;

                // Find matching state from stateData by comparing state names
                const stateEntry = onlyStateData.find(
                  (s) => s.name.toLowerCase() === stateName.toLowerCase()
                );

                const v = stateEntry ? stateEntry.value : 0;
                const fill = v > 0 && max > min ? fillScale(v) : "var(--bg-hover)";

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="var(--border-default)"
                    strokeWidth={0.6}
                    onMouseEnter={(e) =>
                      stateEntry && handleHover(e, stateEntry as any, "state")
                    }
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      default: { outline: "none" },
                      hover: {
                        outline: "none",
                        filter: "brightness(0.95)",
                        cursor: stateEntry ? "pointer" : "default",
                      },
                      pressed: { outline: "none" },
                    }}
                  ></Geography>
                );
              })
            }
          </Geographies>

          {/* Render City Markers (only non-state entries) */}
          {onlyCityData.map((city, idx) => {
            if (!city.coordinates) return null;
            const [lng, lat] = city.coordinates;

            return (
              <Marker key={`${city.name}-${idx}`} coordinates={[lng, lat]}>
                <circle
                  r={bubbleScale(city.value)}
                  fill="var(--blue)"
                  stroke="var(--bg-surface)"
                  strokeWidth={1}
                  onMouseEnter={(e) => handleHover(e, city as any, "city")}
                  onMouseLeave={() => setHovered(null)}
                />
              </Marker>
            );
          })}
        </g>
      </ComposableMap>
      {hovered && (() => {
        const cardWidth = 300;
        const cardHeightEstimate = 150;
        const margin = 12;

        let left = hovered.x + margin;
        if (left + cardWidth > window.innerWidth) {
          left = hovered.x - cardWidth - margin;
        }
        left = Math.max(margin, Math.min(left, window.innerWidth - cardWidth - margin));

        let top = hovered.y + margin;
        if (top + cardHeightEstimate > window.innerHeight) {
          top = hovered.y - cardHeightEstimate - margin;
        }
        top = Math.max(margin, Math.min(top, window.innerHeight - cardHeightEstimate - margin));

        return (
        <div
          style={{
            position: "fixed",
            top,
            left,
            zIndex: 9999,
            background: "var(--bg-elevated)",
            border: "1px solid var(--border-default)",
            color: "var(--text-primary)",
          }}
          className="rounded-2xl shadow-xl w-[300px]"
        >
          <div className="p-4 border-b" style={{ background: "var(--blue-subtle)", borderColor: "var(--border-subtle)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              {hovered.type === "state" ? "State Overview" : "City Overview"}
            </div>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span>State Name</span>
              <strong style={{ color: "var(--blue)" }}>{hovered.name?.toUpperCase()}</strong>
            </div>
            <div className="flex justify-between">
              <span>Sales</span>
              <strong style={{ color: "var(--success)" }}>${hovered.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default WorldUSSalesMap;
